<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { StoppedJourney, TaskErrorStatus } from './HealthManager.types';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import Modal from '@shared/components/Modal.vue';
    import BsAlert from '@shared/components/BsAlert.vue';

    const props = defineProps<{
        stoppedJourneys: StoppedJourney[];
        from: string;
        to: string;
        accountFilter: string;
        loading: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:from', value: string): void;
        (e: 'update:to', value: string): void;
        (e: 'update:accountFilter', value: string): void;
        (e: 'createTaskError', taskId: string): void;
        (e: 'openTaskError', taskId: string, errorCode: string): void;
    }>();

    const filtered = computed(() =>
        props.accountFilter
            ? props.stoppedJourneys.filter(j =>
                  j.AccountName.toLowerCase().includes(props.accountFilter.toLowerCase())
              )
            : props.stoppedJourneys
    );

    function errorCategory(errorCode: string, messageType: string): string {
        const lower = errorCode.toLowerCase();
        if (lower.includes('segment') || lower.includes('contact') || lower.includes('recipient'))
            return 'Missing segment / contacts';
        if (messageType === 'sms')
            return 'SMS error';
        return 'Issue error';
    }

    function errorCategoryClass(errorCode: string, messageType: string): string {
        const lower = errorCode.toLowerCase();
        if (lower.includes('segment') || lower.includes('contact') || lower.includes('recipient'))
            return 'label-warning';
        if (messageType === 'sms')
            return 'label-info';
        return 'label-danger';
    }

    const selectedError = ref<string | null>(null);
    const copied = ref(false);

    function copyError() {
        if (!selectedError.value) return;
        navigator.clipboard.writeText(selectedError.value).then(() => {
            copied.value = true;
            setTimeout(() => (copied.value = false), 2000);
        });
    }

    function statusBadgeClass(status: number): string {
        if (status === TaskErrorStatus.InProgress) return 'label-primary';
        if (status === TaskErrorStatus.Postponed) return 'label-warning';
        if (status === TaskErrorStatus.Solved) return 'label-success';
        return 'label-default';
    }

    function statusBadgeLabel(status: number): string {
        if (status === TaskErrorStatus.InProgress) return 'InProgress';
        if (status === TaskErrorStatus.Postponed) return 'Postponed';
        if (status === TaskErrorStatus.Solved) return 'Solved';
        return 'New';
    }
</script>

<template>
    <div>
        <h3>Stopped journeys</h3>

        <BsAlert
            type="info"
            :dismissible="false"
            content="Errors shown here are cleared automatically on the next successful task run. A journey may still appear after the issue has been resolved until the task successfully runs again." />

        <div class="d-flex gap-sm m-md-b flex-wrap">
            <div class="d-flex gap-xs align-items-center">
                <label class="m-0" style="white-space: nowrap">From</label>
                <input
                    type="date"
                    class="form-control"
                    :value="from"
                    @change="emit('update:from', ($event.target as HTMLInputElement).value)" />
            </div>
            <div class="d-flex gap-xs align-items-center">
                <label class="m-0" style="white-space: nowrap">To</label>
                <input
                    type="date"
                    class="form-control"
                    :value="to"
                    @change="emit('update:to', ($event.target as HTMLInputElement).value)" />
            </div>
            <div class="d-flex gap-xs align-items-center">
                <input
                    type="search"
                    class="form-control"
                    placeholder="Filter by account name"
                    :value="accountFilter"
                    @input="emit('update:accountFilter', ($event.target as HTMLInputElement).value)" />
            </div>
        </div>

        <div v-if="filtered.length === 0" class="alert alert-info">
            No stopped journeys found for the selected period.
        </div>

        <div v-else class="table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th style="white-space: nowrap">Date / Time</th>
                        <th style="white-space: nowrap">Account</th>
                        <th style="white-space: nowrap">Journey</th>
                        <th style="white-space: nowrap">Last edited by</th>
                        <th style="white-space: nowrap">Object Id</th>
                        <th style="white-space: nowrap">Object title</th>
                        <th style="white-space: nowrap">Error category</th>
                        <th style="white-space: nowrap">Error detail</th>
                        <th style="white-space: nowrap">Review</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(journey, i) in filtered" :key="i">
                        <td>{{ formatDateTime(journey.StoppedAt) }}</td>
                        <td>
                            <a :href="'/BackOffice/Accounts/' + journey.AccountId" target="_blank">
                                {{ journey.AccountName }}
                            </a>
                        </td>
                        <td>
                            <a :href="'/Journeys/' + journey.JourneyId" target="_blank">
                                {{ journey.JourneyTitle }}
                            </a>
                        </td>
                        <td>{{ journey.LastModifiedByName }}</td>
                        <td style="max-width: 90px">
                            <small class="text-muted" style="display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap" :title="journey.MessageId">{{ journey.MessageId }}</small>
                        </td>
                        <td>{{ journey.MessageTitle }}</td>
                        <td style="white-space: nowrap">
                            <span class="label" :class="errorCategoryClass(journey.ErrorCode, journey.MessageType)">
                                {{ errorCategory(journey.ErrorCode, journey.MessageType) }}
                            </span>
                        </td>
                        <td>
                            <button class="btn btn-link p-0" style="text-align: left" @click="selectedError = journey.ErrorCode">
                                <span style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block">
                            {{ journey.ErrorCode.substring(0, 60) }}{{ journey.ErrorCode.length > 60 ? '…' : '' }}
                                </span>
                            </button>
                        </td>
                        <td style="white-space: nowrap">
                            <template v-if="journey.TaskErrorId">
                                <button
                                    class="btn btn-link p-0"
                                    @click="emit('openTaskError', journey.TaskId, 'stopped-journey-review')">
                                    <span class="label" :class="statusBadgeClass(journey.TaskErrorStatus!)">
                                        {{ statusBadgeLabel(journey.TaskErrorStatus!) }}
                                    </span>
                                </button>
                            </template>
                            <template v-else>
                                <button
                                    class="btn btn-default btn-xs"
                                    :disabled="loading"
                                    @click="emit('createTaskError', journey.TaskId)">
                                    + Create task error
                                </button>
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <Modal :show="selectedError !== null" title="Error detail" size="lg" :scrollBody="true" :closeOnClickOutside="true" @close="selectedError = null">
        <template #content>
            <pre style="white-space: pre-wrap; word-break: break-all; font-size: 12px">{{ selectedError }}</pre>
        </template>
        <template #button>
            <button class="btn btn-default" @click="copyError">
                {{ copied ? 'Copied!' : 'Copy' }}
            </button>
        </template>
    </Modal>
</template>
